import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import { CreditCardIcon, MegaphoneIcon, UsersRoundIcon, ZapIcon } from 'lucide-react';

import { Avatar } from '@/components/atoms/Avatar/Avatar/Avatar';
import { RowActionIndicator } from '@/components/atoms/RowActionIndicator/RowActionIndicator';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Badge } from '@/components/ui/badge';
import { createSelectionColumn } from '@/components/ui/table/selection-column';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import { AIAgentIcon } from '@/modules/aiAgents/icons/AIAgentIcon';
import { resolveBrainName } from '@/modules/campaigns/utils/resolveBrainName';
import { Brain } from '@/shared/types/brain';
import { Campaign } from '@/shared/types/campaigns';
import { COLUMN_PRESETS } from '@/util/constants';
import { timeFromNow } from '@/util/dateUtils';

import { ActionsCell } from './ActionsCell';
import { ChannelCell } from './ChannelCell';
import {
  getCampaignStatusLabelKey,
  getCampaignStatusVariant,
} from '../../analytics/campaignStatusBadge';

interface ColumnsProps {
  onDeleteClick: (campaignId: string) => void;
  onStopClick: (campaignId: string) => void;
  brains?: Brain[];
}

export const columns = ({
  onDeleteClick,
  onStopClick,
  brains,
}: ColumnsProps): ColumnDef<Campaign>[] => [
  // Always present: this column carries the campaign type icon as well as the checkbox, so
  // dropping it for read-only users would take their type icons with it. Which rows can actually
  // be selected is decided by the `enableRowSelection` predicate on DataTable.Root, not here —
  // with selection off the column renders icons only and no select-all header.
  createSelectionColumn<Campaign>({
    getIcon: (campaign) => (campaign.type === 'payment_recovery' ? CreditCardIcon : MegaphoneIcon),
    getLabel: (campaign) => campaign.name,
  }),
  {
    id: 'name',
    accessorKey: 'name',
    header: ({ column }) => <SortableHeader column={column}>{t('common.name')}</SortableHeader>,
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-3">
          <span className="min-w-0 truncate font-medium">{row.original.name}</span>
          <RowActionIndicator action="view" />
        </div>
      );
    },
    ...COLUMN_PRESETS.FLEX,
    meta: { preset: 'FLEX' },
  },
  {
    id: 'subscribers_count',
    accessorKey: 'subscribers_count',
    header: ({ column }) => (
      <SortableHeader column={column}>{t('campaigns.audience')}</SortableHeader>
    ),
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <UsersRoundIcon className="size-4 text-foreground-muted" />
        <span>{row.original.subscribers_count.toLocaleString()}</span>
      </div>
    ),
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  },
  {
    id: 'channel',
    enableSorting: false,
    header: () => t('common.channel'),
    cell: ({ row }) => <ChannelCell campaign={row.original} />,
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: ({ column }) => <SortableHeader column={column}>{t('common.status')}</SortableHeader>,
    cell: ({ row }) => {
      const status = row.original.status;

      return (
        <Badge type="secondary" flavour={getCampaignStatusVariant(status)} rounded={false}>
          {t(getCampaignStatusLabelKey(status))}
        </Badge>
      );
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  },
  {
    id: 'ai_agent',
    enableSorting: false,
    header: () => t('campaigns.ai_agent_handling'),
    cell: ({ row }) => {
      const campaign = row.original;
      const brainId = campaign.brain_parent_id;

      if (!brainId) {
        return (
          <div className="flex items-center gap-2">
            <ZapIcon size={16} className="text-foreground-muted" />
            <span className="text-sm text-foreground-muted">{t('campaigns.not_assigned')}</span>
          </div>
        );
      }

      // Find the brain from the brains array (for the type-specific icon)
      const brain = brains?.find((b) => b.brain_id === brainId);
      const brainName = resolveBrainName(brainId, brain);

      return (
        <div className="flex items-center gap-2">
          <AIAgentIcon type={brain?.brain_type || 'support'} size={16} />
          <span className="text-sm">{brainName}</span>
        </div>
      );
    },
    ...COLUMN_PRESETS.LG,
    meta: { preset: 'LG' },
  },
  {
    id: 'created',
    accessorFn: (row) => row.created,
    header: ({ column }) => (
      <SortableHeader column={column}>{t('campaigns.date_started')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const campaign = row.original;

      return (
        <div className="flex min-w-0 items-center gap-2">
          {campaign.created_by && <Avatar userId={campaign.created_by} size="small" hideStatus />}
          <TimestampTooltip timestamp={campaign.created}>
            {timeFromNow(campaign.created)}
          </TimestampTooltip>
        </div>
      );
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  },
  {
    id: 'actions',
    enableSorting: false,
    header: () => '',
    cell: ({ row }) => {
      const item = row.original;
      return <ActionsCell item={item} onDeleteClick={onDeleteClick} onStopClick={onStopClick} />;
    },
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
  },
];
